import React from "react";
import { Box, Container, Grid, Typography, Chip } from "@mui/material";
import { Card, SectionTitle } from "../../../common";
import {
  colors,
  spacing,
  typography,
  borderRadius,
} from "../../../../theme/tokens";
import { CASE_STUDIES } from "./caseStudies";
import CaseDetailModal from "./CaseDetailModal";

const CATEGORIES = ["すべて", "受託実績", "自主運用サービス"];

function CaseList() {
  const [category, setCategory] = React.useState("すべて");
  const [selectedSlug, setSelectedSlug] = React.useState<string | null>(null);
  const list =
    category === "すべて"
      ? CASE_STUDIES
      : CASE_STUDIES.filter((caseStudy) => caseStudy.category === category);

  return (
    <Box id="case" sx={{ scrollMarginTop: { xs: "72px", md: "100px" } }}>
      <Container
        maxWidth="lg"
        sx={{ py: { xs: 8, md: 12 }, px: { xs: "16px", md: "10vw" } }}
      >
        <SectionTitle en="CASE" ja="実績紹介" />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: spacing[200],
            mb: spacing[1200],
          }}
        >
          {CATEGORIES.map((c) => (
            <Chip
              key={c}
              label={c}
              onClick={() => setCategory(c)}
              sx={{
                px: spacing[300],
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                backgroundColor:
                  category === c ? colors.primary[600] : colors.neutral[0],
                color: category === c ? colors.neutral[0] : colors.neutral[700],
                border: `1px solid ${
                  category === c ? colors.primary[600] : colors.neutral[200]
                }`,
                "&:hover": {
                  backgroundColor:
                    category === c ? colors.primary[700] : colors.primary[50],
                },
              }}
            />
          ))}
        </Box>

        <Grid container spacing={4}>
          {list.map((caseStudy) => (
            <Grid item xs={12} sm={6} md={4} key={caseStudy.slug}>
              <Box
                role="button"
                tabIndex={0}
                aria-label={`${caseStudy.name}の実績詳細を見る`}
                onClick={() => setSelectedSlug(caseStudy.slug)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedSlug(caseStudy.slug);
                  }
                }}
                sx={{
                  display: "block",
                  cursor: "pointer",
                  height: "100%",
                }}
              >
                <Card
                  interactive
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      aspectRatio: "16 / 10",
                      overflow: "hidden",
                      backgroundColor: colors.neutral[50],
                      borderBottom: `1px solid ${colors.neutral[200]}`,
                    }}
                  >
                    <img
                      src={caseStudy.cover}
                      alt={caseStudy.coverAlt}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "top",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      p: spacing[600],
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box>
                      <Chip
                        label={caseStudy.category}
                        size="small"
                        sx={{
                          fontSize: "11px",
                          fontWeight: typography.fontWeight.medium,
                          backgroundColor: colors.primary[50],
                          color: colors.primary[700],
                          borderRadius: borderRadius.full,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        mt: spacing[300],
                        fontWeight: typography.fontWeight.bold,
                        color: colors.neutral[900],
                      }}
                    >
                      {caseStudy.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: spacing[200],
                        color: colors.neutral[600],
                        lineHeight: typography.lineHeight.relaxed,
                        flexGrow: 1,
                      }}
                    >
                      {caseStudy.summary}
                    </Typography>
                    <Typography
                      sx={{
                        mt: spacing[400],
                        fontSize: typography.fontSize.sm,
                        fontWeight: typography.fontWeight.semibold,
                        color: colors.primary[600],
                      }}
                    >
                      この事例を詳しく見る →
                    </Typography>
                  </Box>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
      <CaseDetailModal
        slug={selectedSlug}
        onClose={() => setSelectedSlug(null)}
        onSelectCase={setSelectedSlug}
      />
    </Box>
  );
}

export default CaseList;
